import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactElement, type ReactNode, useEffect, useRef, useState } from "react";
import {
	DisplayFieldFeedback,
	type DisplayFieldFeedback as DisplayFieldFeedbackType,
} from "@/components/Atoms/DisplayField/display-field.shared";
import {
	FileUpload,
	type UploadedFile,
} from "@/components/application/file-upload/file-upload-base";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { FieldLabel } from "@/components/base/input/field-label";
import { Input } from "@/components/base/input/input";
import { LabelWithFeedback } from "@/components/base/input/label-with-feedback";
import {
	RadioButton,
	RadioGroup,
} from "@/components/base/radio-buttons/radio-buttons";
import { NativeSelect } from "@/components/base/select/select-native";
import { TextArea } from "@/components/base/textarea/textarea";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { FieldRenderer } from "@/lib/dynamicForms/components/FieldRenderer.component";
import { API_DATETIME_DATA_TYPE } from "@/lib/dynamicForms/constants/api-date-time-data-type.constant";
import { useDynamicFormHost } from "@/lib/dynamicForms/contexts/dynamic-form-host-context";
import { useDynamicFormContext } from "@/lib/dynamicForms/contexts/DynamicFormContext";
import type {
	AutocompleteField,
	CheckboxField,
	FieldArrayField,
	FileUploadField,
	FormField,
	GroupField,
	RadioField,
	SelectField,
	SliderField,
	ToggleButtonField,
} from "@/lib/dynamicForms/interfaces/dynamicForm.interface";
import type {
	FieldRendererFunction,
	FieldRendererProps,
	FieldRendererRegistry,
} from "@/lib/dynamicForms/interfaces/renderer.interface";
import { persistedFormFieldToDefinition } from "@/lib/dynamicForms/utils/persisted-field-definition.util";
import { fetchEntityFormFile } from "@/lib/dynamicForms/utils/entity-file-download.util";
import { isCheckedValue } from "@/lib/dynamicForms/utils/is-checked-value.util";
import {
	resolveStoredFilePresentation,
	storedFileDetails,
} from "@/lib/dynamicForms/utils/stored-file-id.util";
import {
	formatPhoneNumber,
	phoneValueForStorage,
} from "@/lib/dynamicForms/utils/phoneFormatter.util";
import { organizationService } from "@/services/organization/organization.service";
import { cx } from "@/utils/cx";
import { downloadBlob } from "@/utils/download";
import { showApiErrorToast } from "@/utils/toast.utils";
import {
	AddressFormControl,
	MultiSelectFormControl,
	SignatureFormControl,
	UsersFormControl,
} from "./additional-field-renderers";

interface RadioFieldViewProps {
	label: string;
	isRequired?: boolean;
	isDisabled?: boolean;
	value: string;
	onChange: (value: string) => void;
	options: Array<{ value: string; label: string }>;
	hint?: ReactNode;
	isInvalid?: boolean;
	/** Auto-save FeedbackIndicator in the label row (injected by job form wrap). */
	feedback?: DisplayFieldFeedbackType;
}

/**
 * Radio group with label-row FeedbackIndicator support (same pattern as Input/Select).
 * Label sits outside RadioGroup so feedback mount/update cannot re-trigger selection.
 */
function RadioFieldView({
	label,
	isRequired,
	isDisabled,
	value,
	onChange,
	options,
	hint,
	isInvalid,
	feedback = DisplayFieldFeedback.Idle,
}: RadioFieldViewProps): ReactElement {
	return (
		<div className="flex flex-col gap-3">
			<LabelWithFeedback
				label={label}
				isRequired={isRequired}
				isFilled={value.trim().length > 0}
				feedback={feedback}
			/>
			{hint && !isInvalid ? (
				<p className="text-sm text-tertiary">{hint}</p>
			) : null}
			<RadioGroup
				isDisabled={isDisabled}
				value={value}
				onChange={onChange}
				aria-label={label}
				className="flex flex-col gap-3"
			>
				{options.map((o) => (
					<RadioButton key={o.value} value={o.value} label={o.label} />
				))}
			</RadioGroup>
			{isInvalid && hint ? (
				<p className="text-sm text-error-600">{hint}</p>
			) : null}
		</div>
	);
}

export interface CheckboxFieldViewProps {
	label: string;
	isRequired?: boolean;
	isDisabled?: boolean;
	isSelected: boolean;
	onChange: (isSelected: boolean) => void;
	hint?: ReactNode;
	isInvalid?: boolean;
	/** Auto-save FeedbackIndicator in the label row (injected by job form wrap). */
	feedback?: DisplayFieldFeedbackType;
}

/**
 * Checkbox with label-row FeedbackIndicator support (same pattern as RadioFieldView).
 * Label sits outside AriaCheckbox so feedback mount/update cannot re-trigger selection.
 */
export function CheckboxFieldView({
	label,
	isRequired,
	isDisabled,
	isSelected,
	onChange,
	hint,
	isInvalid,
	feedback = DisplayFieldFeedback.Idle,
}: CheckboxFieldViewProps): ReactElement {
	return (
		<div className="flex flex-col gap-2">
			<LabelWithFeedback
				label={label}
				isRequired={isRequired}
				isFilled={isSelected}
				feedback={feedback}
			/>
			{hint && !isInvalid ? (
				<p className="text-sm text-tertiary">{hint}</p>
			) : null}
			<Checkbox
				isDisabled={isDisabled}
				isSelected={isSelected}
				onChange={onChange}
				aria-label={label}
			/>
			{isInvalid && hint ? (
				<p className="text-sm text-error-600">{hint}</p>
			) : null}
		</div>
	);
}

function formatFieldError(fieldApi: FieldRendererProps["fieldApi"]): string {
	const err = fieldApi.state.meta.errors[0];
	if (err == null) return "";
	if (typeof err === "string") return err;
	if (
		typeof err === "object" &&
		err !== null &&
		"message" in err &&
		typeof (err as { message: unknown }).message === "string"
	) {
		return (err as { message: string }).message;
	}
	return String(err);
}

function buildDefaultRow(fields: FormField[]): Record<string, unknown> {
	const row: Record<string, unknown> = {};
	for (const f of fields) {
		switch (f.type) {
			case "text":
			case "textArea":
			case "phone":
			case "url":
				row[f.name] = "";
				break;
			case "number":
				row[f.name] = f.validation?.min ?? 0;
				break;
			case "checkbox":
				row[f.name] = false;
				break;
			case "switch":
				row[f.name] = false;
				break;
			case "radio":
			case "select":
				row[f.name] = f.defaultValue ?? "";
				break;
			case "slider":
				row[f.name] = f.defaultValue ?? f.min;
				break;
			case "toggleButton":
				row[f.name] = f.exclusive ? (f.defaultValue ?? "") : [];
				break;
			case "autocomplete":
				row[f.name] = f.multiple ? [] : (f.defaultValue ?? "");
				break;
			case "date":
			case "time":
			case "dateTime":
				row[f.name] = f.defaultValue ?? "";
				break;
			case "file":
				row[f.name] = null;
				break;
			default:
				row[f.name] = "";
		}
	}
	return row;
}

function FieldArrayRenderer({ field, form }: FieldRendererProps) {
	const fa = field as FieldArrayField;

	useEffect(() => {
		const current = form.getFieldValue(fa.name);
		if (current === undefined) {
			form.setFieldValue(fa.name, [] as unknown[]);
		}
	}, [fa.name, form]);

	const items =
		(form.getFieldValue(fa.name) as Record<string, unknown>[] | undefined) ??
		[];

	const add = () => {
		const max = fa.maxItems ?? Number.POSITIVE_INFINITY;
		if (items.length >= max) return;
		form.setFieldValue(fa.name, [...items, buildDefaultRow(fa.fields)]);
	};

	const remove = (index: number) => {
		const min = fa.minItems ?? 0;
		if (items.length <= min) return;
		form.setFieldValue(
			fa.name,
			items.filter((_, i) => i !== index),
		);
	};

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-1">
				<FieldLabel isRequired={fa.required} isFilled={items.length > 0}>
					{fa.label}
				</FieldLabel>
			</div>

			{items.map((_, index) => (
				<div
					key={`${fa.id}-${index}`}
					className={cx(
						"flex flex-col gap-4 rounded-lg border border-secondary bg-primary p-4",
					)}
				>
					{fa.itemLabel ? (
						<p className="text-sm font-medium text-tertiary">
							{fa.itemLabel} {index + 1}
						</p>
					) : null}
					{fa.fields.map((child) => (
						<FieldRenderer
							key={child.id}
							fieldDefinition={persistedFormFieldToDefinition({
								...child,
								name: `${fa.name}.${index}.${child.name}`,
							})}
						/>
					))}
					<Button
						type="button"
						color="secondary"
						size="sm"
						onClick={() => remove(index)}
					>
						{fa.removeButtonText ?? "Remove"}
					</Button>
				</div>
			))}

			<Button type="button" color="secondary" size="sm" onClick={add}>
				{fa.addButtonText ?? "Add"}
			</Button>
		</div>
	);
}

interface AppFileUploadFieldProps
	extends Pick<FieldRendererProps, "field" | "fieldApi"> {
	hint?: ReactNode;
	isInvalid?: boolean;
	/** Auto-save FeedbackIndicator in the label row (injected by job form wrap). */
	feedback?: DisplayFieldFeedbackType;
}

/**
 * File upload field with label-row FeedbackIndicator support (same pattern as Input/Radio).
 */
function AppFileUploadField({
	field,
	fieldApi,
	hint,
	isInvalid,
	feedback = DisplayFieldFeedback.Idle,
}: AppFileUploadFieldProps) {
	const { t } = useAppTranslation();
	const host = useDynamicFormHost();
	const queryClient = useQueryClient();
	const f = field as FileUploadField;
	const details = storedFileDetails(fieldApi.state.value);
	const fileId = details?.id ?? null;
	const uploadGeneration = useRef(0);
	const [sessionFile, setSessionFile] = useState<{
		id: string;
		name: string;
		size: number;
		type: string;
		file: File;
	} | null>(null);
	const [fileAction, setFileAction] = useState<"view" | "download" | null>(
		null,
	);
	const error = formatFieldError(fieldApi);
	const resolvedHint = hint ?? (error || f.hint || undefined);
	const resolvedInvalid = isInvalid ?? fieldApi.state.meta.errors.length > 0;
	const session = sessionFile?.id === fileId ? sessionFile : null;
	const canFetch =
		host?.runtime === true &&
		Boolean(host.organizationId) &&
		Boolean(host.entityType) &&
		Boolean(host.entityId) &&
		Boolean(fileId);
	const remoteQuery = useQuery({
		queryKey: [
			"dynamic-form-file",
			host?.entityType,
			host?.organizationId,
			host?.entityId,
			fileId,
		],
		enabled: canFetch && !session && !details?.name,
		queryFn: () =>
			fetchEntityFormFile(
				{
					entityType: host?.entityType ?? "",
					organizationId: host?.organizationId ?? "",
					entityId: host?.entityId ?? "",
				},
				fileId ?? "",
			),
	});
	const presented = resolveStoredFilePresentation(
		fieldApi.state.value,
		session,
		remoteQuery.data
			? {
					fileName: remoteQuery.data.fileName,
					size: remoteQuery.data.blob.size,
				}
			: null,
	);
	const shownFile: UploadedFile | null = presented
		? { ...presented, progress: 100 }
		: null;

	const handleAddFiles = (fileList: FileList) => {
		const file = fileList.item(0);
		if (!file || !host?.organizationId) return;
		const generation = ++uploadGeneration.current;
		void organizationService
			.uploadTemporaryOrganizationFile(host.organizationId, file)
			.then((id) => {
				if (uploadGeneration.current !== generation) return;
				setSessionFile({
					id,
					name: file.name,
					size: file.size,
					type: file.type && file.type.length > 0 ? file.type : "empty",
					file,
				});
				fieldApi.handleChange(id);
			})
			.catch((uploadError: unknown) => {
				if (uploadGeneration.current !== generation) return;
				showApiErrorToast({
					error: uploadError,
					message: t("common.fileUpload.uploadFailed"),
				});
			});
	};

	const handleDeleteFile = () => {
		uploadGeneration.current += 1;
		setSessionFile(null);
		fieldApi.handleChange(null);
	};

	const openFile = async (action: "view" | "download") => {
		if (!fileId || fileAction) return;
		setFileAction(action);
		try {
			let blob: Blob;
			let fileName: string;
			if (session && session.id === fileId) {
				blob = session.file;
				fileName = session.name;
			} else if (
				canFetch &&
				host?.entityType &&
				host.organizationId &&
				host.entityId
			) {
				const result = await queryClient.fetchQuery({
					queryKey: [
						"dynamic-form-file",
						host.entityType,
						host.organizationId,
						host.entityId,
						fileId,
					],
					queryFn: () =>
						fetchEntityFormFile(
							{
								entityType: host.entityType ?? "",
								organizationId: host.organizationId,
								entityId: host.entityId ?? "",
							},
							fileId,
						),
				});
				blob = result.blob;
				fileName = details?.name ?? result.fileName ?? "file";
			} else {
				return;
			}
			if (action === "download") {
				downloadBlob(blob, fileName);
				return;
			}
			const url = URL.createObjectURL(blob);
			const opened = window.open(url, "_blank", "noopener,noreferrer");
			window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
			if (!opened) downloadBlob(blob, fileName);
		} catch (openError: unknown) {
			showApiErrorToast({
				error: openError,
				message: t("common.fileUpload.openFailed"),
			});
		} finally {
			setFileAction(null);
		}
	};

	const isButton = f.presentation === "button";
	const triggerTitle = f.dropZoneTitle ?? t("common.fileUpload.chooseFile");

	return (
		<div className="flex flex-col gap-2">
			<LabelWithFeedback
				label={f.label}
				isRequired={f.required}
				isFilled={Boolean(fileId)}
				feedback={feedback}
			/>
			{resolvedHint && !resolvedInvalid ? (
				<p className="text-sm text-tertiary">{resolvedHint}</p>
			) : null}
			<FileUpload>
				{isButton ? (
					<FileUpload.ButtonPicker
						isDisabled={f.disabled}
						accept={f.accept}
						multiple={false}
						title={triggerTitle}
						hint={f.dropZoneHint}
						onDropFiles={handleAddFiles}
					/>
				) : (
					<FileUpload.DropZone
						isDisabled={f.disabled}
						accept={f.accept}
						multiple={false}
						title={f.dropZoneTitle}
						hint={f.dropZoneHint}
						onDropFiles={handleAddFiles}
					/>
				)}
				{shownFile ? (
					<FileUpload.List>
						<FileUpload.ListItemProgressBar
							{...shownFile}
							size={shownFile.size}
							onDelete={handleDeleteFile}
						/>
					</FileUpload.List>
				) : null}
				{shownFile && (session || canFetch) ? (
					<div className="flex gap-2">
						<Button
							type="button"
							size="sm"
							color="secondary"
							isLoading={fileAction === "view"}
							isDisabled={fileAction != null}
							onClick={() => void openFile("view")}
						>
							{t("common.actions.view")}
						</Button>
						<Button
							type="button"
							size="sm"
							color="secondary"
							isLoading={fileAction === "download"}
							isDisabled={fileAction != null}
							onClick={() => void openFile("download")}
						>
							{t("common.tableActions.download")}
						</Button>
					</div>
				) : null}
			</FileUpload>
			{resolvedInvalid && resolvedHint ? (
				<p className="text-sm text-error-600">{resolvedHint}</p>
			) : null}
		</div>
	);
}

/** Focus snapshot + Escape discard for continuous (commit-on-leave) fields. */
function useContinuousFieldEscape(fieldName: string, fieldValue: unknown) {
	const ctx = useDynamicFormContext();

	return {
		onFocus: () => {
			ctx?.handleFieldFocus(fieldName, fieldValue);
		},
		onKeyDown: (event: {
			key: string;
			preventDefault: () => void;
			stopPropagation: () => void;
		}) => {
			if (event.key !== "Escape") return;
			event.preventDefault();
			event.stopPropagation();
			ctx?.handleFieldCancel(fieldName);
		},
	};
}

/**
 * Field renderer registry using app base components (Input, NativeSelect, Checkbox, etc.).
 * Pass the result to {@link RendererProvider} or {@link DynamicForm} `fieldRenderers`.
 */
export function createAppFieldRenderers(): FieldRendererRegistry {
	const renderPhoneField: FieldRendererFunction = ({ field, fieldApi }) => {
		const escapeProps = useContinuousFieldEscape(
			field.name,
			fieldApi.state.value,
		);
		return (
			<Input
				label={field.label}
				isRequired={field.required}
				isDisabled={field.disabled}
				isInvalid={fieldApi.state.meta.errors.length > 0}
				hint={formatFieldError(fieldApi) || field.hint || undefined}
				type="tel"
				placeholder={"placeholder" in field ? field.placeholder : undefined}
				value={formatPhoneNumber(String(fieldApi.state.value ?? ""))}
				onChange={(v) => {
					fieldApi.handleChange(phoneValueForStorage(String(v)));
				}}
				onBlur={fieldApi.handleBlur}
				{...escapeProps}
			/>
		);
	};

	const renderDateField: FieldRendererFunction = ({ field, fieldApi }) => (
		<Input
			label={field.label}
			isRequired={field.required}
			isDisabled={field.disabled}
			isInvalid={fieldApi.state.meta.errors.length > 0}
			hint={formatFieldError(fieldApi) || field.hint || undefined}
			type="date"
			value={String(fieldApi.state.value ?? "")}
			onChange={fieldApi.handleChange}
		/>
	);

	const renderTimeField: FieldRendererFunction = ({ field, fieldApi }) => (
		<Input
			label={field.label}
			isRequired={field.required}
			isDisabled={field.disabled}
			isInvalid={fieldApi.state.meta.errors.length > 0}
			hint={formatFieldError(fieldApi) || field.hint || undefined}
			type="time"
			value={String(fieldApi.state.value ?? "")}
			onChange={fieldApi.handleChange}
		/>
	);

	const renderDateTimeField: FieldRendererFunction = ({ field, fieldApi }) => (
		<Input
			label={field.label}
			isRequired={field.required}
			isDisabled={field.disabled}
			isInvalid={fieldApi.state.meta.errors.length > 0}
			hint={formatFieldError(fieldApi) || field.hint || undefined}
			type="datetime-local"
			value={String(fieldApi.state.value ?? "")}
			onChange={fieldApi.handleChange}
		/>
	);

	const renderFileField: FieldRendererFunction = ({ field, fieldApi }) => (
		<AppFileUploadField field={field} fieldApi={fieldApi} />
	);

	return {
		text: ({ field, fieldApi }) => {
			const escapeProps = useContinuousFieldEscape(
				field.name,
				fieldApi.state.value,
			);
			return (
				<Input
					label={field.label}
					isRequired={field.required}
					isDisabled={field.disabled}
					isInvalid={fieldApi.state.meta.errors.length > 0}
					hint={formatFieldError(fieldApi) || field.hint || undefined}
					placeholder={"placeholder" in field ? field.placeholder : undefined}
					value={String(fieldApi.state.value ?? "")}
					onChange={fieldApi.handleChange}
					onBlur={fieldApi.handleBlur}
					{...escapeProps}
				/>
			);
		},

		textarea: ({ field, fieldApi }) => {
			const escapeProps = useContinuousFieldEscape(
				field.name,
				fieldApi.state.value,
			);
			return (
				<TextArea
					label={field.label}
					isRequired={field.required}
					isDisabled={field.disabled}
					rows={field.validation?.rows ?? 3}
					isInvalid={fieldApi.state.meta.errors.length > 0}
					hint={formatFieldError(fieldApi) || field.hint || undefined}
					type="textarea"
					placeholder={"placeholder" in field ? field.placeholder : undefined}
					value={String(fieldApi.state.value ?? "")}
					onChange={fieldApi.handleChange}
					onBlur={fieldApi.handleBlur}
					{...escapeProps}
				/>
			);
		},
		/** Legacy / layout alias for `textarea`. */
		textArea: ({ field, fieldApi }) => {
			const escapeProps = useContinuousFieldEscape(
				field.name,
				fieldApi.state.value,
			);
			return (
				<TextArea
					label={field.label}
					isRequired={field.required}
					isDisabled={field.disabled}
					rows={field.validation?.rows ?? 3}
					isInvalid={fieldApi.state.meta.errors.length > 0}
					hint={formatFieldError(fieldApi) || field.hint || undefined}
					type="textarea"
					placeholder={"placeholder" in field ? field.placeholder : undefined}
					value={String(fieldApi.state.value ?? "")}
					onChange={fieldApi.handleChange}
					onBlur={fieldApi.handleBlur}
					{...escapeProps}
				/>
			);
		},

		url: ({ field, fieldApi }) => {
			const escapeProps = useContinuousFieldEscape(
				field.name,
				fieldApi.state.value,
			);
			return (
				<Input
					label={field.label}
					isRequired={field.required}
					isDisabled={field.disabled}
					isInvalid={fieldApi.state.meta.errors.length > 0}
					hint={formatFieldError(fieldApi) || field.hint || undefined}
					type="url"
					placeholder={"placeholder" in field ? field.placeholder : undefined}
					value={String(fieldApi.state.value ?? "")}
					onChange={fieldApi.handleChange}
					onBlur={fieldApi.handleBlur}
					{...escapeProps}
				/>
			);
		},

		number: ({ field, fieldApi }) => {
			const raw = fieldApi.state.value;
			const display =
				raw === "" || raw === undefined || raw === null ? "" : String(raw);
			const escapeProps = useContinuousFieldEscape(field.name, raw);
			return (
				<Input
					label={field.label}
					isRequired={field.required}
					isDisabled={field.disabled}
					isInvalid={fieldApi.state.meta.errors.length > 0}
					hint={formatFieldError(fieldApi) || field.hint || undefined}
					type="number"
					placeholder={"placeholder" in field ? field.placeholder : undefined}
					value={display}
					onChange={(v) => {
						if (v === "" || v === undefined) {
							fieldApi.handleChange("");
							return;
						}
						const n = Number(v);
						fieldApi.handleChange(Number.isNaN(n) ? v : n);
					}}
					onBlur={fieldApi.handleBlur}
					{...escapeProps}
				/>
			);
		},

		phone: renderPhoneField,
		/** Legacy API `dataType`; same renderer as `phone`. */
		tel: renderPhoneField,

		checkbox: ({ field, fieldApi }) => {
			const f = field as CheckboxField;
			const error = formatFieldError(fieldApi);
			return (
				<CheckboxFieldView
					label={f.label}
					isRequired={f.required}
					isDisabled={f.disabled}
					isSelected={isCheckedValue(fieldApi.state.value)}
					onChange={fieldApi.handleChange}
					hint={error || f.hint || undefined}
					isInvalid={fieldApi.state.meta.errors.length > 0}
				/>
			);
		},

		radio: ({ field, fieldApi }) => {
			const f = field as RadioField;
			const error = formatFieldError(fieldApi);
			return (
				<RadioFieldView
					label={f.label}
					isRequired={f.required}
					isDisabled={f.disabled}
					value={String(fieldApi.state.value ?? "")}
					onChange={fieldApi.handleChange}
					options={f.options}
					hint={error || f.hint || undefined}
					isInvalid={fieldApi.state.meta.errors.length > 0}
				/>
			);
		},

		select: ({ field, fieldApi }) => {
			const f = field as SelectField;
			const opts = [
				{
					value: "",
					label: String(f.placeholder ?? "Select…"),
					disabled: f.required === true,
				},
				...f.options.map((o) => ({
					value: o.value,
					label: o.label,
				})),
			];

			if (f.multiple) {
				const value = Array.isArray(fieldApi.state.value)
					? fieldApi.state.value
					: [];
				return (
					<NativeSelect
						label={f.label}
						isRequired={f.required}
						multiple
						disabled={f.disabled}
						value={value as string[]}
						options={f.options.map((o) => ({
							value: o.value,
							label: o.label,
						}))}
						onChange={(e) => {
							const selected = Array.from(e.target.selectedOptions).map(
								(o) => o.value,
							);
							fieldApi.handleChange(selected);
						}}
						hint={formatFieldError(fieldApi) || f.hint || undefined}
					/>
				);
			}

			return (
				<NativeSelect
					label={f.label}
					isRequired={f.required}
					disabled={f.disabled}
					value={String(fieldApi.state.value ?? "")}
					options={opts}
					onChange={(e) => fieldApi.handleChange(e.target.value)}
					hint={formatFieldError(fieldApi) || f.hint || undefined}
				/>
			);
		},

		slider: ({ field, fieldApi }) => {
			const f = field as SliderField;
			const min = f.min;
			const max = f.max;
			const step = f.step ?? 1;
			const val = Number(fieldApi.state.value ?? min);
			return (
				<div className="flex flex-col gap-2">
					<FieldLabel
						isRequired={f.required}
						isFilled={Number.isFinite(val)}
						htmlFor={f.id}
					>
						{f.label}
					</FieldLabel>
					{f.hint && !fieldApi.state.meta.errors.length ? (
						<p className="text-sm text-tertiary">{f.hint}</p>
					) : null}
					<input
						id={f.id}
						type="range"
						min={min}
						max={max}
						step={step}
						disabled={f.disabled}
						value={Number.isNaN(val) ? min : val}
						onChange={(e) => fieldApi.handleChange(Number(e.target.value))}
						className="w-full accent-brand"
					/>
					<span className="text-sm text-tertiary">
						{Number.isNaN(val) ? min : val}
					</span>
					{fieldApi.state.meta.errors.length > 0 ? (
						<p className="text-sm text-error-600">
							{formatFieldError(fieldApi)}
						</p>
					) : null}
				</div>
			);
		},

		switch: ({ field, fieldApi }) => {
			const error = formatFieldError(fieldApi);
			return (
				<CheckboxFieldView
					label={field.label}
					isRequired={field.required}
					isDisabled={field.disabled}
					isSelected={isCheckedValue(fieldApi.state.value)}
					onChange={fieldApi.handleChange}
					hint={error || field.hint || undefined}
					isInvalid={fieldApi.state.meta.errors.length > 0}
				/>
			);
		},

		toggleButton: ({ field, fieldApi }) => {
			const f = field as ToggleButtonField;
			if (f.exclusive) {
				const error = formatFieldError(fieldApi);
				return (
					<RadioFieldView
						label={f.label}
						isRequired={f.required}
						isDisabled={f.disabled}
						value={String(fieldApi.state.value ?? "")}
						onChange={fieldApi.handleChange}
						options={f.options}
						hint={error || f.hint || undefined}
						isInvalid={fieldApi.state.meta.errors.length > 0}
					/>
				);
			}

			const selected = Array.isArray(fieldApi.state.value)
				? (fieldApi.state.value as string[])
				: [];

			const toggle = (value: string, checked: boolean) => {
				if (checked) {
					fieldApi.handleChange([...selected, value]);
				} else {
					fieldApi.handleChange(selected.filter((v) => v !== value));
				}
			};

			return (
				<div className="flex flex-col gap-3">
					<FieldLabel isRequired={f.required} isFilled={selected.length > 0}>
						{f.label}
					</FieldLabel>
					{f.hint && !fieldApi.state.meta.errors.length ? (
						<p className="text-sm text-tertiary">{f.hint}</p>
					) : null}
					<div className="flex flex-col gap-2">
						{f.options.map((o) => (
							<Checkbox
								key={o.value}
								isDisabled={f.disabled}
								isSelected={selected.includes(o.value)}
								onChange={(checked) => toggle(o.value, checked)}
								label={o.label}
							/>
						))}
					</div>
					{fieldApi.state.meta.errors.length > 0 ? (
						<p className="text-sm text-error-600">
							{formatFieldError(fieldApi)}
						</p>
					) : null}
				</div>
			);
		},

		autocomplete: ({ field, fieldApi }) => {
			const f = field as AutocompleteField;
			if (f.freeSolo) {
				return (
					<Input
						label={f.label}
						isRequired={f.required}
						isDisabled={f.disabled}
						isInvalid={fieldApi.state.meta.errors.length > 0}
						hint={formatFieldError(fieldApi) || f.hint || undefined}
						placeholder={f.placeholder}
						value={String(fieldApi.state.value ?? "")}
						onChange={fieldApi.handleChange}
					/>
				);
			}

			const opts = [
				{ value: "", label: String(f.placeholder ?? "Select…") },
				...f.options.map((o) => ({ value: o.value, label: o.label })),
			];

			if (f.multiple) {
				const value = Array.isArray(fieldApi.state.value)
					? fieldApi.state.value
					: [];
				return (
					<NativeSelect
						label={f.label}
						isRequired={f.required}
						multiple
						disabled={f.disabled}
						value={value as string[]}
						options={f.options.map((o) => ({
							value: o.value,
							label: o.label,
						}))}
						onChange={(e) => {
							const selected = Array.from(e.target.selectedOptions).map(
								(o) => o.value,
							);
							fieldApi.handleChange(selected);
						}}
						hint={formatFieldError(fieldApi) || f.hint || undefined}
					/>
				);
			}

			return (
				<NativeSelect
					label={f.label}
					isRequired={f.required}
					disabled={f.disabled}
					value={String(fieldApi.state.value ?? "")}
					options={opts}
					onChange={(e) => fieldApi.handleChange(e.target.value)}
					hint={formatFieldError(fieldApi) || f.hint || undefined}
				/>
			);
		},

		group: ({ field }) => {
			const g = field as GroupField;
			if (!g.fields?.length) {
				return null;
			}

			return (
				<section
					className="flex w-full flex-col gap-(--spacing-lg,12px)"
					aria-label={g.label}
				>
					{g.label ? (
						<h3 className="text-md font-extrabold text-primary">{g.label}</h3>
					) : null}
					<div className="flex flex-col gap-(--spacing-3xl,24px)">
						{g.fields.map((child) => (
							<FieldRenderer
								key={child.id}
								fieldDefinition={persistedFormFieldToDefinition(child)}
							/>
						))}
					</div>
				</section>
			);
		},

		fieldArray: (props) => <FieldArrayRenderer {...props} />,

		date: renderDateField,
		/** Legacy API `dataType` */
		datePicker: renderDateField,

		time: renderTimeField,
		timePicker: renderTimeField,

		[API_DATETIME_DATA_TYPE]: renderDateTimeField,
		/** Legacy API `dataType` */
		dateTime: renderDateTimeField,
		dateTimePicker: renderDateTimeField,

		file: renderFileField,
		fileUpload: renderFileField,
		multiSelect: (props) => <MultiSelectFormControl {...props} />,
		multiselect: (props) => <MultiSelectFormControl {...props} />,
		users: (props) => <UsersFormControl {...props} />,
		signature: (props) => <SignatureFormControl {...props} />,
		address: (props) => <AddressFormControl {...props} />,
	};
}
